import React, { useEffect, useMemo, useState } from 'react';
import './OrderListing.css';
import { onValue, ref, remove, set } from 'firebase/database';
import { toast } from 'react-toastify';
import { db } from '../../firebase';
import Sidebar from '../../components/sidebar/Sidebar';
import { SearchOutlined } from '@mui/icons-material';
import { FaEdit, FaEye, FaTrash } from 'react-icons/fa';
import AddNewOrderModal from './AddNewOrderModal';
import EditOrderModal from './EditOrderModal';
import ViewOrderModal from './ViewOrderModal';

const OrderListing = () => {
    const [orders, setOrders] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState({ add: false, edit: false, view: false });
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    const itemsPerPage = 10;

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const ordersRef = ref(db, 'Orders/');
                onValue(ordersRef, (snapshot) => {
                    setLoading(false);
                    setOrders(snapshot.val() || []);
                });
            } catch (error) {
                setLoading(false);
                toast.error('Error fetching orders');
                console.error(error);
            }
        };
        fetchOrders();
    }, []);

    const handleSearch = (e) => setSearchQuery(e.target.value);

    const filteredOrders = useMemo(() => {
        if (!searchQuery) return orders;
        return Object.fromEntries(
            Object.entries(orders).filter(([id, order]) =>
                order.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase())
                || order.invoice_id?.toLowerCase().includes(searchQuery.toLowerCase())

            )
        );
    }, [searchQuery, orders]);

    const currentOrders = useMemo(() => {
        const begin = (currentPage - 1) * itemsPerPage;
        const slicedKeys = Object.keys(filteredOrders)
            .reverse()  //reverse the order
            .slice(begin, begin + itemsPerPage);

        return slicedKeys.reduce((acc, key) => {
            acc[key] = filteredOrders[key];
            return acc;
        }, {});
    }, [currentPage, filteredOrders]);

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this order?')) {
            const orderRef = ref(db, `Orders/${id}`);
            remove(orderRef)
                .then(() => toast.success('Order deleted successfully.'))
                .catch((error) => {
                    toast.error('Error deleting order');
                    console.error(error);
                });
        }
    };

    const handleAddOrder = (newOrder) => {
        const orderRef = ref(db, `Orders/${newOrder.invoice_id}`);
        set(orderRef, newOrder)
            .then(() => {
                toast.success('Order added successfully.');
                setShowModal((prev) => ({ ...prev, add: false }));
            })
            .catch((error) => {
                toast.error('Error adding order');
                console.error(error);
            });
    };

    const handleUpdateOrder = (updatedOrder) => {
        const orderRef = ref(db, `Orders/${updatedOrder.invoice_id}`);
        set(orderRef, updatedOrder)
            .then(() => {
                toast.success('Order updated successfully.');
                setShowModal((prev) => ({ ...prev, edit: false }));
            })
            .catch((error) => {
                toast.error('Error updating order');
                console.error(error);
            });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Start': return '#e0e006';
            case 'In Progress': return '#cc1a1a';
            case 'Completed': return '#4d9d4d';
            default: return 'grey';
        }
    };

    return (
        <div className="list">
            <Sidebar />
            <div className="listContainer">
                <div className="datatable">
                    <div className="datatableTitle">
                        <h2>Order Listing</h2>
                        <button onClick={() => setShowModal((prev) => ({ ...prev, add: true }))} className="link">
                            Add New Order
                        </button>
                    </div>

                    <div className="search">
                        <input
                            type="text"
                            placeholder="Search by order name..."
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                        <SearchOutlined className="searchIcon" />
                    </div>

                    {loading ? (
                        <p>Loading orders...</p>
                    ) : Object.keys(filteredOrders).length === 0 ? (
                        <p>No orders found</p>
                    ) : (
                        <div className="orderCards">
                            {Object.keys(currentOrders).map((id) => (
                                <div className="orderCard" key={id}>
                                    <h3
                                        style={{
                                            backgroundColor: getStatusColor(orders[id].order_status),
                                            padding: '5px',
                                            borderRadius: '5px',
                                            color: 'white',
                                        }}
                                    >
                                        {orders[id].invoice_number}
                                    </h3>
                                    <p>Invoice ID: {id}</p>
                                    <p>Reference:{orders[id].reference}</p>                                    <p>Description: {orders[id].order_description}</p>
                                    <p>Status: {orders[id].order_status}</p>
                                    <p>Added On: {new Date(orders[id].added_date).toLocaleString()}</p>
                                    <p>Products: {
                                        Array.isArray(orders[id].products)
                                            ? orders[id].products.map(product => product.name).join(',')
                                            : 'N/A'
                                    }
                                    </p>

                                    <div className="ol-actions">
                                        <button onClick={() => {
                                            setSelectedOrder(orders[id]);
                                            setShowModal((prev) => ({ ...prev, edit: true }));
                                        }}><FaEdit /></button>
                                        <button onClick={() => {
                                            setSelectedOrder(orders[id]);
                                            setShowModal((prev) => ({ ...prev, view: true }));
                                        }}><FaEye /></button>
                                        <button onClick={() => handleDelete(id)}><FaTrash /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="pagination">
                        <button
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        Page {currentPage} of {Math.ceil(Object.keys(filteredOrders).length / itemsPerPage)}
                        <button
                            onClick={() =>
                                setCurrentPage((prev) =>
                                    Math.min(prev + 1, Math.ceil(Object.keys(filteredOrders).length / itemsPerPage))
                                )
                            }
                            disabled={currentPage === Math.ceil(Object.keys(filteredOrders).length / itemsPerPage)}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            <AddNewOrderModal
                show={showModal.add}
                handleClose={() => setShowModal((prev) => ({ ...prev, add: false }))}
                handleAddOrder={handleAddOrder}
            />
            <ViewOrderModal
                show={showModal.view}
                handleClose={() => setShowModal((prev) => ({ ...prev, view: false }))}
                order={selectedOrder}
                handleEdit={(order) => {
                    setShowModal((prev) => ({ ...prev, view: false })); // Close ViewOrderModal
                    setSelectedOrder(order); // Set the order to be edited
                    setShowModal((prev) => ({ ...prev, edit: true })); // Open edit modal
                }}
            />
            <EditOrderModal
                show={showModal.edit}
                handleClose={() => setShowModal((prev) => ({ ...prev, edit: false }))}
                orderToEdit={selectedOrder} // Pass selectedOrder as orderToEdit
                handleUpdateOrder={handleUpdateOrder}
            />
        </div>
    );
};

export default OrderListing;
